import Button from "@/components/shared/ui/Button";
import Link from "next/link";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";

interface TermsOfServicesComponentProps {
	usageContext: "page" | "signup-form";
	closeFn?: () => void;
	handleSubmitForm?: () => void;
}

export default function TermsOfServices(props: TermsOfServicesComponentProps) {
	const { usageContext, closeFn, handleSubmitForm } = props;

	useEffect(() => {
		const handleScroll = (event: any) => {
			const targetId = event.target.id.replace("link-to-section-", "");

			const targetElement = document.getElementById(`${targetId}-section`);

			if (targetElement) {
				targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
			}
		};

		const contentLinks = document.querySelectorAll(".content-linker");
		contentLinks.forEach((item) => {
			item.addEventListener("click", handleScroll);
		});

		return () => {
			contentLinks.forEach((item) => {
				item.removeEventListener("click", handleScroll);
			});
		};
	}, []);

	const [height, setHeight] = useState(0);
	const contentIndexSectionRef = useRef(null);

	useLayoutEffect(() => {
		// @ts-ignore
		setHeight(contentIndexSectionRef?.current?.clientHeight);
	}, [setHeight]);

	return (
		<div className="terms-of-use-container w-full space-y-4 pb-12">
			{usageContext == "signup-form" && (
				<Button className="flex items-center gap-3 px-6 py-1.5" variant="outline" onClick={closeFn}>
					<FaArrowLeft /> Go back
				</Button>
			)}

			<h1 className="mb-4 !text-3xl font-bold">Terms of Service</h1>
			<p className="!text-sm text-gray-600">
				ONESHOT.EARTH INC | OPEN CARBON PROTOCOL <br />
				Version 2.01 <br />
				Last Updated October 2024
			</p>

			<div className="!mt-12 space-y-6">
				<p className="">
					Welcome to oneshot.earth’s Open Carbon Protocol! The Open Carbon Protocol (or "
					<span className="tou-highlight-span">OCP</span>") is a new registry facilitating the creation and approval of
					new methodologies and validated projects, as well as the holding, transferring and retiring of OCP Carbon
					Credits. It is created and maintained by oneshot.earth ("<span className="tou-highlight-span">OSE</span>").
				</p>

				<p className="">
					These terms of service are a legal agreement (this "<span className="tou-highlight-span">Agreement</span>")
					between you (together with the business entity, if any, that you represent, "
					<span className="tou-highlight-span">You</span>" or "<span className="tou-highlight-span">Your</span>" or "
					<span className="tou-highlight-span">User</span>") and oneshot.earth, establishing terms and conditions under
					which You shall access and use the services and features available on OCP. The date You first agree to or
					accept this Agreement, or that You otherwise first access and use OCP following the date this Agreement is
					first made available on OCP, is referred to herein as the "Effective Date".
				</p>

				{usageContext == "signup-form" && (
					<p className="">
						BEFORE YOU CLICK ON THE "I ACCEPT" OR SIMILAR BUTTON OR ACCESS OR USE OCP, CAREFULLY READ THE TERMS AND
						CONDITIONS OF THIS AGREEMENT.
					</p>
				)}

				{usageContext == "signup-form" && (
					<p className="">
						BY CLICKING ON THE "I ACCEPT" BUTTON OR USING THE SERVICES OR ACCESSING THE WEBSITE, YOU AND THE BUSINESS
						ENTITY THAT YOU REPRESENT ARE AGREEING TO BE BOUND BY AND ARE BECOMING A PARTY TO THIS AGREEMENT. YOU
						REPRESENT AND WARRANT THAT YOU HAVE THE AUTHORITY TO ENTER INTO THIS AGREEMENT ON BEHALF OF THE BUSINESS
						ENTITY YOU REPRESENT. YOU HEREBY COVENANT TO ENSURE THAT ALL USERS THAT GAIN ACCESS TO OCP FROM YOU ARE
						BOUND BY THIS AGREEMENT. IF YOU DO NOT AGREE TO ALL OF THE TERMS OF THIS AGREEMENT, THEN DO{" "}
						<span className="underline">NOT</span> CLICK "I ACCEPT" OR A SIMILAR INDICATION OF AGREEMENT AND YOU SHALL
						NOT BE PERMITTED TO ACCESS AND/OR USE OCP.
					</p>
				)}

				<p className="">
					YOU ARE SOLELY RESPONSIBLE FOR ALL CONTENT THAT YOU SUBMIT IN CONNECTION WITH OCP. YOU MUST PROVIDE US ONLY
					CANDIDATE METHODOLOGIES, CANDIDATE PROJECTS AND OTHER DATA OR CONTENT FOR WHICH YOU HAVE ALL RIGHTS NECESSARY
					TO COPY, USE, MODIFY AND EXPLOIT SUCH CONTENT AS CONTEMPLATED IN THIS AGREEMENT OR ANY ORDER. YOU SHALL
					INDEMNIFY AND HOLD HARMLESS OSE, ITS OFFICERS, DIRECTORS, EMPLOYEES AND SERVICE PROVIDERS FROM AND AGAINST ANY
					AND ALL THIRD PARTY CLAIMS THAT THE SUBMISSION OR USE OF THE CONTENT YOU PROVIDE INFRINGES, MISAPPROPRIATES OR
					VIOLATES THE INTELLECTUAL PROPERTY OR OTHER PROPRIETARY RIGHTS OF ANY THIRD PARTY.
				</p>

				<p className="">See below for more detail, or read the full document any time here.</p>
			</div>

			<hr className="!my-12 border-t border-gray-300" />

			<div className="terms-of-use-content-container sticky flex" style={{ height: height }}>
				<div className="sticky h-fit w-1/3 pt-0" ref={contentIndexSectionRef}>
					<p className="mb-4 !font-roboto-mono !text-2xl">Contents</p>
					<ol className="!text-md list-inside list-decimal space-y-3 text-[0.94em] font-normal">
						<li id="link-to-section-services" className="content-linker cursor-pointer">
							Services
						</li>
						<li id="link-to-section-account-opening-and-closing" className="content-linker cursor-pointer">
							Account Opening and Closing
						</li>
						<li id="link-to-section-methodology-development" className="content-linker cursor-pointer">
							Methodology Development, Feedback and Approval
						</li>
						<li id="link-to-section-project-proposal-validation" className="content-linker cursor-pointer">
							Project Proposal and Validation
						</li>
						<li id="link-to-section-credit-issuance" className="content-linker cursor-pointer">
							Credit Issuance + Credit Ownership
						</li>
						<li id="link-to-section-credit-transfer" className="content-linker cursor-pointer">
							Credit Transfer
						</li>
						<li id="link-to-section-credit-retirement" className="content-linker cursor-pointer">
							Credit Retirement
						</li>
						<li id="link-to-section-fees" className="content-linker cursor-pointer">
							Fees
						</li>
						<li id="link-to-section-user-representations" className="content-linker cursor-pointer">
							User Representations, Warranties and Covenants
						</li>
						<li id="link-to-section-ocp-representations" className="content-linker cursor-pointer">
							OCP Representations, Warranties and Covenants
						</li>
						<li id="link-to-section-term-termination" className="content-linker cursor-pointer">
							Term, Termination and Suspension
						</li>
						<li id="link-to-section-user-content" className="content-linker cursor-pointer">
							User Content
						</li>
						<li id="link-to-section-intellectual-property" className="content-linker cursor-pointer">
							Intellectual Property
						</li>
						<li id="link-to-section-confidential-information" className="content-linker cursor-pointer">
							Confidential Information
						</li>
						<li id="link-to-section-limitations-liabilities" className="content-linker cursor-pointer">
							Limitations of Liabilities
						</li>
						<li id="link-to-section-indemnification" className="content-linker cursor-pointer">
							Indemnification
						</li>
						<li id="link-to-section-notices" className="content-linker cursor-pointer">
							Notices
						</li>
						<li id="link-to-section-amendments" className="content-linker cursor-pointer">
							Amendments
						</li>
						<li id="link-to-section-force-majeure" className="content-linker cursor-pointer">
							Force Majeure
						</li>
						<li id="link-to-section-governing-law" className="content-linker cursor-pointer">
							Governing Law, Dispute Resolution + Attorney’s Fees
						</li>
						<li id="link-to-section-subcontractors" className="content-linker cursor-pointer">
							Subcontractors
						</li>
						<li id="link-to-section-definitions" className="content-linker cursor-pointer">
							Definitions
						</li>
					</ol>
				</div>

				<main className="scrollbar-hide w-2/3 overflow-y-scroll p-6">
					<ol className="numbered-list space-y-8">
						{/*Services*/}
						<li id="services-section">
							<span className="text-2xl">Services</span>
							<ol className="!mt-4 space-y-6">
								<p>
									The Open Carbon Protocol ("<span className="tou-highlight-span">OCP</span>") is a Platform available
									at{" "}
									<Link href={"https://ocp.earth"} className="text-blue-400 underline">
										https://ocp.earth
									</Link>{" "}
									created and maintained by oneshot.earth ("
									<span className="tou-highlight-span">OSE</span>") that allows registered Users ("
									<span className="tou-highlight-span">User</span>") to access and use three tools: (1) Methodology
									Proposal, Feedback + Approval; (2) Project Proposal, Validation, Approval and Issuance; and (3)
									Transfer or Retire Credits on the OCP Registry ( "
									<span className="tou-highlight-span">OCP Registry</span>").
								</p>

								<p>
									The ‘Methodology Proposal, Feedback + Approval’ tool allows Individuals or organizations ("
									<span className="tou-highlight-span">Methodology Curators</span>") to propose methodologies for the
									creation of carbon credit projects ("
									<span className="tou-highlight-span">Proposed Methodologies</span>"). These Candidate Methodologies
									will be reviewed by a panel ("Panel") of experts (each, an "
									<span className="tou-highlight-span">Expert</span>") and feedback will be given and discussed on the
									OCP platform. A subset of the Panel will vote ("
									<span className="tou-highlight-span">Voting Expert Panel</span>") on whether to approve or reject the
									Candidate Methodology. Approved Methodologies shall be published on the OCP platform ("
									<span className="tou-highlight-span">Approved Methodologies</span>"). Each Expert will provide their
									services under an agreement between such Expert and OSE; however, OSE will not have any input or
									decision-making authority in the decisions or comments of those Experts or the Panel nor any other
									input into the Candidate Methodology.
								</p>

								<p>
									The ‘Project Proposal, Validation + Approval’ tool allows registered Project Developers ("
									<span className="tou-highlight-span">Project Developers</span>") to use Approved Methodologies to
									propose carbon reduction or removal projects ("
									<span className="tou-highlight-span">Proposed Projects</span>"), get projects validated ("
									<span className="tou-highlight-span">Approved Projects</span>") by a certified third party validation
									body ("
									<span className="tou-highlight-span">Validator</span>") and issue carbon credits ("
									<span className="tou-highlight-span">Credit Issuance</span>").
								</p>

								<p>
									Registered Users ("<span className="tou-highlight-span">Known Users</span>") can hold, transfer
									through a purchase or sale ("<span className="tou-highlight-span">Credit Transfer</span>") and retire
									("<span className="tou-highlight-span">Credit Retirement</span>") carbon credits ("
									<span className="tou-highlight-span">Carbon Credit</span>") on the OCP Registry platform.
								</p>
								<p>
									OCP may allow Users to optionally link all or a portion of their User Content or account data to third
									party organizations via an Application Programming Interface (API). All agreements between Users
									utilizing APIs and such third-party organizations are fully independent of these Terms of Service.
								</p>
							</ol>
						</li>

						{/*Account Opening and Closing*/}
						<li id="account-opening-and-closing-section">
							<span className="text-2xl">Account Opening and Closing</span>
							<ol className="!mt-4 space-y-6">
								<li>
									In order to open an account on the OCP or use the OCP Registry or any of the tools provided through
									the Platform, these Terms of Service must be accepted.
								</li>
								<li>
									In order to open or upgrade to a ‘Known’ Account, which allows you to (a) propose a new Project, (b)
									provide data for a Project, and (c) hold, transfer and retire credits, the following requirements must
									be met:
									<ol className="!mt-4 space-y-6">
										<li>All requirements listed in 2.1</li>
										<li>
											User Account represents a business, corporation or organization, not an individual (natural
											person).
										</li>
										<li>
											User Account is not acting on behalf of, or as an agent of, any other third-party organization or
											individual.
										</li>
										<li>
											User agrees to undergo Know-Your-Business with our third party KYB Provider and provide sufficient
											identification information.
										</li>
									</ol>
								</li>
								<li>
									In order to open a Validator account, which allows you to validate Candidate Projects, the following
									requirements must be met:
									<ol className="!mt-4 space-y-6">
										<li>All requirements listed in 2.1 and 2.2.</li>
										<li>
											User has completed the Validator Application Form and has been approved by OCP Admin. As part of
											the application, User has to provide accurate and truthful information on their sector expertise
											and accredited information.
										</li>
									</ol>
								</li>
								<li>If this information materially changes, User must inform the OCP immediately in writing.</li>
								<li>
									OCP reserves the right to refuse to open a new account, or suspend access to an existing account, for
									any reason at any time.
								</li>
							</ol>
						</li>

						{/*Methodology Development, Feedback and Approval*/}
						<li id="methodology-development-section">
							<span className="text-2xl">Methodology Development, Feedback and Approval</span>
							<ol className="!mt-4 space-y-6">
								<li>
									<span className="underline">Methodology Content.</span> The Methodology Curator, not OSE, shall be
									solely responsible for the accuracy, quality, integrity, legality, reliability, appropriateness of,
									and the parties’ respective rights to use all Methodology Content under this Agreement. Client shall
									be responsible for obtaining any consents and other rights necessary to collect, submit, use, and
									process Methodology Content on OCP and for OSE to exercise the rights to Methodology Content set forth
									in this Agreement.
								</li>
								<li>
									<span className="underline">License to Methodology Content.</span> Methodology Curator grants to OSE a
									royalty-free, nonexclusive, irrevocable, perpetual, fully paid-up, sublicensable (through one or more
									tiers), transferable, worldwide right and license to the Methodology Content for any purpose until the
									expiration or termination of this Agreement. Aggregate or de-identified data means data that does not
									identify any Client or individual.
								</li>
								<li>
									<span className="underline">Disclosure of Methodology Content.</span> OSE may disclose Methodology
									Content with prior approval of the Methodology Curator.
								</li>
								<li>
									<span className="underline">Consumers and Clients.</span> Consumers and Clients shall have license
									rights to use the Client Content, Expert Feedback, and Approved Methodology according to terms and
									conditions set forth on the OCP from time to time. You agree and acknowledge that OSE may, but is not
									required to, license the Client Content, Expert Feedback, and Approved Methodology subject to
									open-source or other licenses. IN ANY EVENT, ANY USE OF THE CLIENT CONTENT, EXPERT FEEDBACK, OR
									APPROVED METHODOLOGY WILL BE AT YOUR SOLE RISK, WITHOUT ANY WARRANTIES, EXPRESS OR IMPLIED.
								</li>
								<li>
									<span className="underline">Use of Expert Feedback and Approved Methodologies.</span> You use any
									Expert Feedback and Approved Methodologies at your sole risk. You acknowledge that OSE merely provides
									a platform for independent third parties to submit and evaluate Candidate Methodologies and to
									communicate public information regarding such Candidate Methodologies and any resulting Approved
									Methodologies. OSE does not approve, test, or validate any Candidate Methodology or Expert Feedback.
								</li>
								<li>
									<span className="underline">Expert Agreement.</span> Each Expert will provide their services under an
									agreement between such Expert and OSE; however, OSE will not have any input or decision-making
									authority in the decisions or comments of those Experts nor any other input into the Candidate
									Methodology. The Expert Agreement supersedes any conflicting provision in these Terms of Use,
									otherwise both Agreements must be upheld.
								</li>
							</ol>
						</li>

						{/*Project Proposal and Validation*/}
						<li id="project-proposal-validation-section">
							<span className="text-2xl">Project Proposal and Validation</span>
							<ol className="!mt-4 space-y-6">
								<li>
									In order to register a Project on the OCP Registry, and issue credits from that project, Users are
									required to:
									<ol className="!mt-4 space-y-6">
										<li>Have an approved "Known" OCP account, having undergone relevant KYB checks.</li>
										<li>Submit an accurate Project Proposal, including all relevant documentation and project data.</li>
										<li>
											Undergo Validation with an accredited third party Validator, who will review the project
											documentation and submit a Validation Report for Information or Documents, on the OCP platform. At
											the end of Validation, if the Validator approves the Validation Statement, the Project is approved
											and published on the OCP Registry ("<span className="tou-highlight-span">Approved Project</span>
											"). If they do not approve, Project is not Approved. If a User wishes to resubmit a new version of
											the same Project, this will begin a new Project Proposal process.
										</li>
										<li>
											Ensure compliance with all relevant laws and regulations related to the Project and geography.
										</li>
									</ol>
								</li>
								<li>
									OCP reserves the right to refuse to approve a Project or to revoke any Approved Project, for any
									reason at any time.
								</li>
							</ol>
						</li>

						{/*Credit Issuance + Credit Ownership*/}
						<li id="credit-issuance-section">
							<span className="text-2xl">Credit Issuance + Credit Ownership</span>

							<ol className="!mt-4 space-y-6">
								<li>
									On or immediately after the Monitoring Dates outlined in the Project Proposal for an Approved Project,
									Developer and Data Provider partners are responsible for submitting relevant Monitored Data for the
									time period.
								</li>
								<li>
									Once data has been confirmed by the OCP or relevant third parties, carbon credits equal to the
									calculated emission reduction or removal will be issued into the Developer’s inventory of carbon
									credits ("<span className="tou-highlight-span">Inventory</span>").
								</li>
								<li>
									User must not issue or attempt to issue credits or other instructions on another platform for the same
									Project for the period in which such Project is available through the OCP Registry.
								</li>
								<li>
									Any Users that have met all requirements in 2.2 (Developer, Data Provider, Buyer, Validator) can own
									carbon credits on the OCP Registry.
								</li>
								<li>
									Pre-Approved Sub-Registry Users can assign Beneficial Ownership ("
									<span className="tou-highlight-span">Beneficial Owner</span>") of a credit to another third party
									organization. The Beneficial Owner must be an organization and not an individual. Sub-Registry Users
									must perform KYB on all beneficial owners and, if asked, provide the output of the KYB process. The
									Sub-Registry User retains the right to transfer ownership to other Users or other Beneficial Owners,
									but must inform the OCP before the ownership is recorded in the registry. Sub-Registry Users must pay
									the relevant fees.
								</li>
							</ol>
						</li>

						{/*Credit Transfer*/}
						<li id="credit-transfer-section">
							<span className="text-2xl">Credit Transfer</span>

							<ol className="!mt-4 space-y-6">
								<li>
									Any User with carbon credits in their inventory can transfer those carbon credits to another account,
									by requesting a transfer on the OCP platform. These credits will be given the status ‘on-hold’ during
									the transfer process, and cannot be transferred or retired until the process is completed.
								</li>
								<li>
									The first transfer of a credit is subject to a transaction fee, based on credit transfer quantity and
									price sold at. This fee must be paid to the OCP before the credit is transferred to the new Inventory.
									See Section 8 for more information.
								</li>
								<li>
									Upon notification of the transfer request and upon confirmation that the transaction fee has been
									paid, the OCP will begin the transfer process:
									<ol className="!mt-4 space-y-6">
										<li>
											If the transfer is to an existing Known OCP Account, the credits will be transferred to that
											account.
										</li>
										<li>
											If the transfer is to a buyer without a Known OCP Account, that buyer will be required to open a
											Known account on the OCP in order to complete such transfer. If they are unwilling or unable to
											open an account, the transfer will be reversed, credits will be returned to the original account,
											and the transaction fee will be refunded.
										</li>
									</ol>
								</li>
								<li>
									OCP reserves the right to pause or reject a credit transfer for any reason at any time. The credit
									will be returned to the original User’s Inventory, unless fraudulent activity is suspected, in which
									case the credits will remain On-Hold until the issue is resolved.
								</li>
							</ol>
						</li>

						{/*Credit Retirement*/}
						<li id="credit-retirement-section">
							<span className="text-2xl">Credit Retirement</span>
							<ol className="!mt-4 space-y-6">
								<li>
									Any User with carbon credits in their inventory can Retire them. Once credits are Retired, they are
									permanently transferred to the OCP ‘Retired Credit’ account and no more transactions can be performed
									on them.
								</li>
								<li>The User agrees that all carbon credit Retirements are permanent and cannot be reversed.</li>
								<li>
									Any User holding carbon credits for a third party Beneficial Owner can Retire credits on behalf of the
									Beneficial Owner.
								</li>
								<li>
									The User or Beneficial Owner is responsible for ascertaining and claiming any benefits associated with
									the Retirement of credits for the use of Environmental Ledgers, emission offsetting, Beyond Value
									Chain Mitigation or any other purpose.
								</li>
							</ol>
						</li>

						{/*Fees*/}
						<li id="fees-section">
							<span className="text-2xl">Fees</span>
							<ol className="!mt-4 space-y-6">
								<li>
									<span className="underline">Fees</span>.
									<ol className="!mt-4 space-y-6">
										<li>
											The first transfer from the assigned Project Developer’s Inventory for each Project is subject to
											a 2% transaction fee, based on the credit transfer quantity and price sold. This fee must be paid
											to the OCP before the credit is transferred to the new Inventory.
										</li>
										<li>
											OSE may change the Fees in its sole discretion at any time; provided that Fees for Client’s
											then-current Transfers will not change until the expiration of such then-current Transfers. Users
											will be notified in writing of changes to fees.
										</li>
									</ol>
								</li>
								<li>
									<span className="underline">Payment</span>. All fees must be paid prior to the commencement of the
									relevant service. All amounts payable under this Agreement will be made without setoff or
									counterclaim, and without any deduction.
								</li>
								<li>
									<span className="underline">Taxes</span>. User shall pay all applicable sales (unless an exemption
									certificate is furnished by User to OSE), use and value-added taxes (except for taxes imposed on OSE’s
									net income) with respect to this Agreement or furnish OSE with evidence acceptable to the taxing
									authority to sustain an exemption therefrom. All payments under this Agreement shall be made free and
									clear of (and without deduction for or grossed up for, as applicable) any withholding or other taxes
									levied by any country or jurisdiction on payments to be made pursuant to this Agreement that
									applicable law requires User to withhold.
								</li>
								<li>
									<span className="underline">Payment Processing</span>. OSE uses third party payment processor services
									to bill and/or facilitate processing of Fees. By submitting payment account information, User grants
									to OSE and its payment processor the right to store and process User’s information with the
									third-party payment service, which may change from time to time. User agrees that OSE will not be
									responsible for any failure of the third party to adequately protect such information. Any payment
									processor services will be subject to the payment processor’s separate terms and conditions in
									addition to this Agreement. In no event will OSE be responsible for any action or omission of any
									payment processor, including as to whether any payment is sent or received or sent or received to a
									party other than the intended party. Client agrees that OSE may change the third-party payment service
									and move Client’s information to other service providers.
								</li>
								<li>
									User is liable for any and all fees incurred as a result of the use of the OCP Registry through the
									User Account, whether authorized by the User or not.
								</li>
							</ol>
						</li>

						{/*User Representations, Warranties and Covenants*/}
						<li id="user-representations-section">
							<span className="text-2xl">User Representations, Warranties and Covenants</span>
							<ol className="!mt-4 space-y-6">
								<li>
									User warrants that User has the right, including in respect of all relevant Intellectual Property
									Rights and applicable data privacy and other laws, to provide OSE access to and use of the User
									Content, including without limitation, for us in connection with the OCP
								</li>
								<li>
									User warrants that it is duly organized, validly existing, and in good standing under the laws of the
									relevant jurisdictions.
								</li>
								<li>
									User represents and warrants that (i) User has full power and authority to enter into this Agreement
									and to perform its obligations; (ii) User’s execution of and performance under this Agreement shall
									not breach any oral or written agreement with a third party to keep any Information or Documentation
									in confidence or in trust; (iii) User has the right to grant all rights in the Proposed Methodology,
									Approved Methodology, Proposed Project, and Approved Project, contemplated by this Agreement.
								</li>
								<li>
									While Users may assign Beneficial Ownership of their Credits, User must not act as an agent of, nor
									perform transactions on behalf of, an unknown third party. User may not assign Beneficial Ownership to
									a company that is the target of US sanctions.
								</li>

								<li>
									User must maintain their log-in credentials in confidence, and allow only employees and recognized
									representatives access to the account. If the User suspects unauthorized access using their account,
									they should notify the OCP team immediately.
								</li>

								<li>
									User shall not, directly or indirectly, and User shall not permit any User or third party to: (i)
									reverse engineer, decompile, disassemble or otherwise attempt to discover the object code, source code
									or underlying ideas or algorithms of OCP; (ii) modify, translate, or create derivative works based on
									any element of OCP or any related documentation; (iii) rent, lease, distribute, sell, resell, assign,
									or otherwise transfer its rights to use OCP; (iv) remove any proprietary notices from the
									Documentation; (vi) publish or disclose to third parties any evaluation of the OCP without OSE’s prior
									written consent; (vii) use the OCP for any purpose other than its intended purpose; (viii) interfere
									with or disrupt the integrity or performance of the OCP; (ix) attempt to gain unauthorized access to
									the Services or OCP; or (x) upload or other transmit any viruses or other harmful, disruptive or
									destructive files.
								</li>
								<li>
									User covenants that they will comply with these Terms of Service, all other procedures set out by OSE,
									and all applicable laws.
								</li>

								<li>
									<span className="underline">User acknowledges that:</span>.
									<ol className="!mt-4 space-y-6">
										<li>
											OSE/OCP has no special fiduciary responsibility to the User and these Terms of Service do not
											create any partnership, joint venture, employment, franchise or other relationship.
										</li>
										<li>
											All credit transactions are performed by the User and the relevant third parties, with separate
											agreements.
										</li>

										<li>
											OSE/OCP does not assume any responsibility for the performance or settlement of a transaction.
										</li>
										<li>
											Once Methodologies have been approved by the Expert Panel, they are publicly available on the OCP
											registry. Any subsequent changes or additions to the information, by either Methodology Curator or
											OCP, shall be posted as an update, and not replace the original Methodology information.
										</li>
										<li>
											Once Project information has been approved by a Validator and is publicly available on the OCP
											registry, that project information cannot be removed or altered. Any subsequent changes or
											additions to the information shall be posted as an update, and not replace the original Project
											information.
										</li>
										<li>User must inform OSE/OCP of any incorrect or inaccurate information on the OCP Registry.</li>
										<li>
											User is responsible for obtaining, deploying and maintaining all hardware, software, modems,
											routers and other communications equipment necessary for the User to access and use the OCP via
											the Internet. Except as specifically set forth in this Agreement, OSE shall not be responsible for
											supplying any hardware, software, or other equipment to the User.
										</li>
									</ol>
								</li>
							</ol>
						</li>

						{/*Ocp Representations, Warranties and Covenants*/}
						<li id="ocp-representations-section" className="section-point-over-nine">
							<span className="text-2xl">Ocp Representations, Warranties and Covenants</span>
							<ol className="!mt-4 space-y-6">
								<li>
									OCP warrants to User that the OCP Platform and Services, under normal use and service, substantially
									conform to, and perform in all material respects, the functions described by the OCP and this
									Agreement. If any such Platform or services fail to comply with the foregoing warranty, User shall
									provide written notice to OSE describing such failure. In such event, OSE shall use reasonable efforts
									to repair or rectify the failure. If OSE is unable to repair or rectify the failure, then OSE may
									terminate this Agreement.
									<ol className="!mt-4 space-y-6">
										<li>
											<span className="underline">Exclusions</span>. The warranty set forth in 10.1 does not cover
											defects or failures arising from: (i) misuse of OCP or any Information or Documentation displayed
											on the OCP; (ii) any modifications to the OCP made by any person or entity other than OSE that is
											not previously approved by OSE; (iii) any use of the Platform or Services by User beyond the scope
											of the express rights licenses granted in this Agreement; (iv) any use of OCP in combination with
											other software, hardware or data; (v) OSE’s compliance with User’s request for changes to OCP or
											User’s designs.
										</li>
									</ol>
								</li>
								<li>
									OCP covenants that it will comply with all applicable laws, regulations and orders under which it may
									be subject, and possesses all necessary licenses, permits, authorizations and approvals of relevant
									authorities.
								</li>
								<li>OSE/OCP does not warrant that the OCP software is free of bugs and errors.</li>
								<li>
									OCP may, at its sole discretion and with or without cause or prior notice, temporarily or permanently
									stop operating the OCP Registry and/or temporarily or permanently stop Credit Issuance or Transfer.
								</li>
							</ol>
						</li>

						{/*Term, termination and suspension*/}
						<li id="term-termination-section" className="section-point-over-nine">
							<span className="text-2xl">Term, termination and suspension</span>
							<ol className="!mt-4 space-y-6">
								<li>
									The term of this Agreement shall commence on the Effective Date and continue until the termination of
									these Terms of Service by either User or OSE, unless earlier terminated as provided in this Agreement.
								</li>
								<li>
									OSE may terminate these Terms of Service by giving 30 days notice in writing to the User, except in
									material breach of these Terms, in which case OSE may terminate these Terms of Service immediately.
								</li>
								<li>User may terminate these Terms of Service by giving 30 days notice in writing to OSE.</li>
								<li>
									Further, either party may terminate this Agreement upon written notice to the other party in the event
									the other party: (a) becomes insolvent or bankrupt or admits its inability to pay its debts as they
									mature, makes an assignment for the benefit of its creditors or ceases to function as a going concern
									or to conduct its operations in the normal course of business and such termination shall occur
									immediately upon notice; or (b) commits a material breach of any provision of this Agreement and does
									not remedy such breach within thirty (30) days after receipt of notice from the non-defaulting party
									or such other period as the parties may agree.
								</li>
								<li>
									<span className="underline">Effects of Termination</span>. Upon termination of these Terms of Service:
									(a) User’s access to the OCP will cease and their account closed, as described in 2.5; (b) all fees
									and other amounts owed under this Agreement shall be immediately due and payable by User.
								</li>
								<li>
									If these Terms of Service are terminated, this section and listed sections will survive according to
									their respective terms: 7 Retirement; 8 Fees; 9 User Representation, Warranties & Covenants; 10 OCP
									Representation,Warranties & Covenants, 11.5 Effects of Termination, 12 User Content, 13 Intellectual
									Property, 15 Limitation of Liability, 16 Indemnification, 22 Definitions.
								</li>
							</ol>
						</li>

						{/*User content*/}
						<li id="user-content-section" className="section-point-over-nine">
							<span className="text-2xl">User content</span>
							<ol className="!mt-4 space-y-6">
								<li>
									User, not OSE, shall be solely responsible for the accuracy, quality, integrity, legality,
									reliability, appropriateness of and the parties’ respective rights to use all User Content under this
									Agreement. User shall be responsible for obtaining any consents and other rights necessary to collect,
									submit, use and process Use Content on OCP and for OSE to exercise the rights to User Content set
									forth in this Agreement.
									<ol className="!mt-4 space-y-6">
										<li>
											<span className="underline">Compliance with Laws</span>. User represents and warrants that it will
											ensure that User’s use of the Services complies with all applicable laws, rules and regulations
											and this Agreement.
										</li>
										<li>
											<span className="underline">Storage of User Contents</span>. User agrees and acknowledges that OCP
											is not a storage service and User is solely responsible for backing up all User Content and any
											other information. OCP does not warrant that User Content will be available for any period of
											time.
										</li>
									</ol>
								</li>
								<li>
									<span className="underline">Usage Data</span>. Notwithstanding anything else in the Agreement or
									otherwise, OSE may monitor User’s use of the Services and OCP and retain and use data and information
									related to User Content and User’s use of the Services, including in an aggregate or de-identified
									manner, to compile statistical and performance information related to the provision, features,
									functionality and/or operation of OCP and the Services. User agrees that OSE may make such data and
									information publicly available, and use such information to the extent and in the manner required by
									applicable law or regulation and/or for purposes of data gathering, analysis, service enhancement and
									marketing, provided that such data and information does not identify User or its Confidential
									Information. OSE retains all Intellectual Property Rights in such data and information.
								</li>
							</ol>
						</li>

						{/*Intellectual Property*/}
						<li id="intellectual-property-section" className="section-point-over-nine">
							<span className="text-2xl">Intellectual Property</span>
							<ol className="!mt-4 space-y-6">
								<li>
									<span className="underline">Ownership</span>. User acknowledges that the OCP, and all Intellectual
									Property Rights therein, are the sole and exclusive property of OSE and its licensors. OSE
									acknowledges that User Content, and all Intellectual Property Rights therein, are the sole and
									exclusive property of User and its licensors. User is allowed to use OSE and OCP names, logo and
									branding on marketing and sales material, unless OSE has withdrawn approval. Each party retains all
									other rights not expressly granted in this Agreement.
								</li>
								<li>
									<span className="underline">License to Methodology Content</span>. See section 3.2.
								</li>
								<li>
									Users shall have license rights to use the User Content, Expert Feedback and Approved Methodology
									according to terms and conditions set forth on the OCP from time to time. You agree and acknowledge
									that OSE may, but is not required to, license the User Content, Expert Feedback and Approved
									Methodology subject to open source or other licenses. IN ANY EVENT, ANY USE OF THE USER CONTENT,
									EXPERT FEEDBACK OR APPROVED METHODOLOGY WILL BE AT YOUR SOLE RISK, WITHOUT ANY WARRANTIES, EXPRESS OR
									IMPLIED.
								</li>
							</ol>
						</li>

						{/*Confidential Information*/}
						<li id="confidential-information-section" className="section-point-over-nine">
							<span className="text-2xl">Confidential Information</span>
							<ol className="!mt-4 space-y-6">
								<li>
									User may provide Confidential Information on the OCP platform within the operations of service. All
									Confidential Information should be labeled as such. OCP agrees to use and maintain Confidential
									Information to accordance with these Terms of Service.
								</li>
								<li>
									The following Information shall always be subject to public disclosure: Project ID; Project Sector and
									Subsector; dates of Monitoring Events; number of Issued Credits at each Monitoring Event; Credit ID;
									Credit Owner; number of Retired Credits; purpose of Retirement.
								</li>
								<li>
									OSE/OCP and User shall each use commercially reasonable efforts to protect the confidentiality of all
									Confidential Information provided during this Agreement.
								</li>
								<li>
									Neither OSE/OCP nor User shall be deemed to have breached these Terms of Service if the use or
									disclosure of Confidential Information is:
									<ol className="!mt-4 space-y-6">
										<li>Authorized in writing.</li>
										<li>
											Reasonably necessary to perform its obligations or comply with applicable laws or regulations.
										</li>
									</ol>
								</li>
							</ol>
						</li>

						{/*Limitations of liabilities*/}
						<li id="limitations-liabilities-section" className="section-point-over-nine">
							<span className="text-2xl">Limitations of liabilities</span>
							<ol className="!mt-4 space-y-6">
								<li>
									<span className="underline">No Consequential Damages</span>. OSE and its licensors shall not be liable
									for any indirect, incidental, special, consequential or punitive damages, or any damages for lost
									data, business interruption, lost profits, lost revenue or lost business, arising out of or in
									connection with this agreement, even if ose or its licensors have been advised of the possibility of
									such damages, including without limitation, any such damages arising out of the licensing, provision
									or use of OCP, services or client content. OSE shall not be liable for the cost of procurement of
									substitute goods or services.
								</li>
								<li>
									<span className="underline">Limits on Liability</span>. OSE and its licensors shall not be liable for
									cumulative, aggregate damages greater than the greater of (a) ten U.S. dollars ($10) and (b) the sum
									of the amounts having then actually been paid by client to ose under this agreement during the twelve
									(12) month period preceding the date the claim arose, minus, in all circumstances, any amounts
									previously paid (as of the date of satisfaction of such liability) by OSE to client in satisfaction of
									any liability for damages under this agreement. Client releases OSE and its licensors from all
									obligations, liability, claims, or demands relating to the OCP, services, client content, expert
									feedback and approved methodology, and this agreement in excess of the limitation provided for in this
									section 15.2.
								</li>
								<li>
									<span className="underline">Essential Purpose</span>. Client acknowledges that the terms in this
									Section 15 (Limitation of Liability) are an essential basis of the bargain described in this Agreement
									and that, were OSE to assume any further liability, the fees payable hereunder would out of necessity,
									be set much higher. THE LIMITATIONS IN THIS SECTION 15 (LIMITATION OF LIABILITY) SHALL APPLY TO THE
									MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW AND SHALL APPLY EVEN IF AN EXCLUSIVE OR LIMITED REMEDY
									STATED HEREIN FAILS OF ITS ESSENTIAL PURPOSE.
								</li>
							</ol>
						</li>

						{/*Indemnification*/}
						<li id="indemnification-section" className="section-point-over-nine">
							<span className="text-2xl">Indemnification</span>
							<ol className="!mt-4 space-y-6">
								<li>
									User shall indemnify and hold harmless, and at OSE’s request defend, OSE and its Affiliates,
									licensors, successors and assigns (and its and their officers, directors, employees, contractors,
									customers, customers and agents) from and against any and all claims, losses, liabilities, damages,
									settlements, expenses and costs (including, without limitation, attorneys’ fees and court costs) which
									arise out of or relate to: (a) any third party claim or threat thereof that the Client Content (and
									the exercise of the rights by OSE granted herein with respect thereto) infringes, misappropriates or
									violates any third party’s Intellectual Property Rights; (b) Client’s use or alleged use of the
									Services or OCP (other than claims arising from OSE’s breach of this Agreement); (c) any transactions
									between User and any third party, or (d) any breach or alleged breach by User of any of its covenants,
									representations or warranties set forth in this Agreement. OSE shall notify User promptly of any claim
									or liability for which indemnification is sought, provided, however, that the failure to give such
									notice shall not relieve User of its obligations hereunder except to the extent that User was actually
									and materially prejudiced by such failure. User may not settle any claim for which indemnification is
									sought under this Section without the prior written approval of OSE, whose approval shall not be
									unreasonably withheld or delayed.
								</li>
							</ol>
						</li>

						{/*Notices*/}
						<li id="notices-section" className="section-point-over-nine">
							<span className="text-2xl">Notices</span>
							<ol className="!mt-4 space-y-6">
								<li>
									Whenever, under the terms of or in connection with this Agreement, any notice, consent, approval,
									authorization or other information is proper or required to be given by either party, such notice,
									consent, approval, authorization or other information shall be in writing and made by email to the
									User Account email address on file, or OCP administrative email account (admin@ocp.earth), or made by
									reputable overnight courier with documentation of receipt to the intended recipient thereof or by
									registered or certified mail, return receipt requested, and with all postage prepaid, to the current
									address (17.2), or to such other address for either party as may be supplied by notice given in
									accordance herewith.
								</li>
								<li>
									Current oneshot.earth mailing address:
									<div className="flex flex-col">
										<p>65 Greene St, Floor 5</p>
										<p>New York, NY, 10012</p>
										<p>United States of America</p>
									</div>
								</li>
							</ol>
						</li>

						{/*Amendments*/}
						<li id="amendments-section" className="section-point-over-nine">
							<span className="text-2xl">Amendments</span>
							<ol className="!mt-4 space-y-6">
								<li>
									OSE may modify these Terms of Service from time to time at their sole discretion. Any such changes
									take effect at the time specified in the modification.
								</li>
								<li>
									Where modification is considered material, OSE will notify User via email and notice on the OSE or OCP
									website. User’s continued use of the OCP Registry indicates acceptance of modified Terms of Service.
									User must ensure compliance with any modification by all people using the User’s Account.
								</li>
								<li>
									<span className="underline">Severability</span>. If any provision of this Agreement is held invalid or
									unenforceable for any reason, the remainder of the provision shall be amended to achieve as closely as
									possible the economic effect of the original term and all other provisions shall continue in full
									force and effect.
								</li>
							</ol>
						</li>

						{/*Force majeure*/}
						<li id="force-majeure-section" className="section-point-over-nine">
							<span className="text-2xl">Force Majeure</span>
							<ol className="!mt-4 space-y-6">
								<li>
									Neither party shall be liable for any failure or delay in performance under this Agreement due to
									fire, explosion, earthquake, storm, flood or other weather; unavailability of necessary utilities or
									raw materials; Internet service provider failures or delays, or denial of service attacks; war, civil
									unrest, acts of terror, insurrection, riot, acts of God or the public enemy; strikes or other labor
									problems; any law, act, order, proclamation, decree, regulation, ordinance, or instructions of
									government or other public authorities, or judgment or decree of a court of competent jurisdiction
									(not arising out of breach by such party of this Agreement); or any other event beyond the reasonable
									control of the party whose performance is to be excused.
								</li>
							</ol>
						</li>

						{/*Governing law, dispute resolution, and attorney’s fees*/}
						<li id="governing-law-section" className="section-point-over-nine">
							<span className="text-2xl">Governing Law, Dispute Resolution + Attorney’s Fees</span>
							<ol className="!mt-4 space-y-6">
								<li>
									This Agreement and the rights and obligations of the parties to and under this agreement shall be
									governed by and construed under the laws of the State of New York as applied to agreements entered
									into and to be performed in such State without giving effect to conflicts of laws rules or principles
									to the extent such rules or principles would apply the laws of any other state or country. The parties
									agree that the United Nations Convention on Contracts for the International Sale of Goods is
									specifically excluded from application to this Agreement.
								</li>
								<li>
									<span className="underline">Disputes</span>. For any disputes arising out of this Agreement, the
									parties consent to exclusive jurisdiction and venue in the state and federal courts located in New
									York, NY USA.
								</li>
								<li>
									<span className="underline">Attorneys’ Fees</span>. In any action to enforce this Agreement, the
									prevailing party shall be awarded all court costs and reasonable attorneys’ fees incurred, including
									such costs and attorneys’ fees incurred in enforcing and collecting any judgment.
								</li>
							</ol>
						</li>

						{/*Subcontractors*/}
						<li id={"subcontractors-section"} className="section-point-over-nine">
							<span className="text-2xl">Subcontractors</span>
							<ol className="!mt-4 space-y-6">
								<li>
									OSE may utilize subcontractors and subprocessors ("
									<span className="tou-highlight-span">Subcontractors</span>") in connection with the activities
									contemplated by this Agreement including generating Expert Feedback. OSE is not responsible for the
									acts or omissions of any Expert, including the approval or rejection of any Candidate Methodology.
								</li>
							</ol>
						</li>

						{/*Definitions*/}
						<li id="definitions-section" className="section-point-over-nine">
							<span className="text-2xl">Definitions</span>
							<ol className="!mt-4 space-y-6">
								<li>
									"<span className="underline">Approved Methodologies</span>" means any Proposed Methodology, together
									with any revisions thereto, approved by the applicable Voting Expert Panel by majority vote or such
									other threshold as may be established on OCP from time to time.
								</li>
								<li>
									"<span className="underline">Approved Projects</span>" means any Proposed Project, together with any
									revisions thereto, approved by the applicable Validator.
								</li>
								<li>
									"<span className="underline">Beneficial Owner</span>" means any contractual or other right to control
									the sale, transfer or retirement of the specified carbon credit.
								</li>
								<li>
									"<span className="underline">Carbon Credit</span>" means a certificate issued by the Open Carbon
									Protocol representing an action defined by an Approved Methodology and certified by a Validator that
									one ton of CO2e has been avoided, reduced or removed.
								</li>
								<li>
									"<span className="underline">Documentation</span>" means any proprietary user documentation made
									available to User by OSE, as amended or updated by OSE from time to time at its discretion.
								</li>
								<li>
									"<span className="underline">Expert</span>" means any User who has been assigned by OSE to an Expert
									Panel for the purpose of assessing a Proposed Methodology.
								</li>
								<li>
									"<span className="underline">Expert Feedback</span>" means all comments and other information directly
									or indirectly provided by any of the Experts through OCP.
								</li>
								<li>
									"<span className="underline">Fees</span>" means the fees set forth by OSE from time to time.
								</li>
								<li>
									"<span className="underline">Inventory</span>" means the User’s owned credits that are available for
									Transfer or Retirement.
								</li>
								<li>
									"<span className="underline">Known User</span>" means a User that has undergone KYB and been approved.
								</li>
								<li>
									"<span className="underline">KYB</span>" means Know Your Business, a verification that confirms the
									legal status of a company and its compliance with Anti-Money Laundering and other regulations.
								</li>
								<li>
									"<span className="underline">Methodology Curator</span>" means any User that submits a Proposed
									Methodology and is responsible for updates and iterations.
								</li>
								<li>
									"<span className="underline">Proposed Methodologies</span>" means a proposal for a methodology that
									allows creation of carbon credit projects.
								</li>
								<li>
									"<span className="underline">Proposed Projects</span>" means a proposal for any activity that delivers
									GHG reduction or removal benefits.
								</li>
								<li>
									"<span className="underline">User Content</span>" means content uploaded to and which may be stored in
									OCP by the User in connection with User’s use of the OCP, including without limitation any Proposed
									Methodologies or Proposed Project, and information regarding User’s use of OCP.
								</li>
								<li>
									"<span className="underline">Validator</span>" means a third party Validation and Verification Body
									(or equivalent organization) who has been approved to validate Proposed Projects on the OCP.
								</li>
								<li>
									"<span className="underline">Voting Expert Panel</span>" means the subsection of the Experts assigned
									to a Proposed Methodology that have voting rights over whether the Methodology should be Approved or
									Rejected.
								</li>
							</ol>
						</li>
					</ol>
					<p className="mt-8">oneshot.earth Inc. Version 2.01 &copy; 2024.</p>
					<p className="mt-4 w-full text-center">[End of Terms and Use]</p>
					{usageContext == "signup-form" && (
						<div className="!my-12 !mb-2 flex justify-end">
							<Button className="flex items-center gap-3" variant="solid" onClick={handleSubmitForm}>
								Agree and Sign Up
								<FaArrowRight />
							</Button>
						</div>
					)}
				</main>
			</div>

			<div className="!mb-12"></div>
		</div>
	);
}
